import { useState } from 'react'
import { useAuth } from './AuthContext'
import { postConsent, type AuthorizeParams } from '../api/oauth'
import { apiErrorMessage } from '../api/client'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { ErrorBanner } from '../components/ErrorBanner'

// OAuthConsent renders the post-signin consent panel that completes the
// MCP-facing OAuth flow (CLAUDE.md §6). The user has already signed in
// via Login.tsx; this panel just asks "let <client> connect on your
// behalf?" and routes the answer through POST /oauth/authorize/consent.
//
// The server's response carries an absolute redirect URL (the client's
// callback with code+state, or with error=access_denied). We navigate
// there with window.location.assign so the SPA router doesn't fight an
// off-origin destination.
export function OAuthConsent({
  params,
  clientName,
}: {
  params: AuthorizeParams
  clientName: string
}) {
  const auth = useAuth()
  const [busy, setBusy] = useState<'approve' | 'deny' | 'switch' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const send = async (approve: boolean) => {
    setError(null)
    setBusy(approve ? 'approve' : 'deny')
    try {
      const { redirect_to } = await postConsent(params, approve, auth.idTokenProvider)
      window.location.assign(redirect_to)
    } catch (e) {
      setBusy(null)
      setError(apiErrorMessage(e, 'Something went wrong. Please try again.'))
    }
  }

  // Sign out WITHOUT declining the grant. Login.tsx re-renders the signin
  // form because auth.user flips to null while the return_to/oauth params
  // stay in the URL, so after signing in as a different user the consent
  // panel resumes automatically. This is the escape hatch when the cached
  // session is the wrong account or carries an unverified email (the CA3
  // gate blocks Allow on email_verified=false) — Cancel still keeps its
  // OAuth meaning (decline → redirect back with error=access_denied).
  const switchAccount = async () => {
    setError(null)
    setBusy('switch')
    try {
      await auth.signOut()
      // On success this component unmounts (auth.user → null) — no nav needed.
    } catch {
      setBusy(null)
      setError('Could not sign out. Please try again.')
    }
  }

  const display = clientName.trim() || 'an external application'

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <h1 className="mb-1 text-2xl font-semibold">Authorize access</h1>
        <p className="mb-6 text-sm text-cream/70">
          <span className="font-medium text-cream">{display}</span> wants to
          act on your Backlit account.
        </p>

        {error && <ErrorBanner className="mb-4">{error}</ErrorBanner>}

        <ul className="mb-6 space-y-2 text-sm text-cream/80">
          <li className="flex items-start gap-2">
            <span aria-hidden className="mt-[2px] text-glow-600">•</span>
            <span>List, read, and update your glows</span>
          </li>
          <li className="flex items-start gap-2">
            <span aria-hidden className="mt-[2px] text-glow-600">•</span>
            <span>Read and write your glows&apos; stored data</span>
          </li>
          <li className="flex items-start gap-2">
            <span aria-hidden className="mt-[2px] text-glow-600">•</span>
            <span>Deploy new versions on your behalf</span>
          </li>
          <li className="flex items-start gap-2">
            <span aria-hidden className="mt-[2px] text-glow-600">•</span>
            <span>Rotate signing keys when you ask it to</span>
          </li>
        </ul>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            className="flex-1"
            disabled={busy !== null}
            onClick={() => send(true)}
          >
            {busy === 'approve' ? 'Connecting…' : 'Allow'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={busy !== null}
            onClick={() => send(false)}
          >
            {busy === 'deny' ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>

        <p className="mt-4 text-center text-xs text-muted">
          Signed in as {auth.user?.email ?? 'unknown'}.{' '}
          <button
            type="button"
            className="font-medium text-glow-600 hover:underline disabled:opacity-50"
            disabled={busy !== null}
            onClick={switchAccount}
          >
            {busy === 'switch' ? 'Signing out…' : 'Use a different account'}
          </button>
        </p>
      </Card>
    </div>
  )
}
